import { PublicSearchBar } from "@/components/search/PublicSearchBar";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { ContactFooterLink } from "./ContactFooterLink";

const IMG_LOGO_DEBOSSED = "/images/homepage/logo-debossed.svg";

type FooterLink =
  | { label: string; href: string; external?: boolean; action?: never }
  | { label: string; action: "contact"; href?: never; external?: never };

const footerColumns: { title: string; links: FooterLink[] }[] = [
  {
    title: "PRODUCT",
    links: [
      { label: "Integrations", href: "/integrations" },
      { label: "Templates", href: "/templates" },
      { label: "Blog", href: "/blog" },
      { label: "Pricing", href: "/pricing" },
      { label: "Docs", href: "https://docs.b3.fun", external: true },
    ],
  },
  {
    title: "COMPANY",
    links: [
      { label: "About", href: "/about" },
      { label: "Company", href: "/company" },
      { label: "Contact", action: "contact" },
      { label: "Terms & conditions", href: "/terms" },
      { label: "Privacy", href: "/privacy" },
    ],
  },
  {
    title: "FOLLOW US",
    links: [
      { label: "X/Twitter", href: "https://x.com/b3dotfun", external: true },
      { label: "YouTube", href: "https://www.youtube.com/@B3OS_AI", external: true },
      { label: "Linkedin", href: "https://www.linkedin.com/company/b3-labs-inc/", external: true },
    ],
  },
];

const linkClass = "text-ds-text-secondary hover:text-ds-text-primary leading-label text-xl transition-colors";

export function HomepageFooter({ hideGrids = false }: { hideGrids?: boolean } = {}) {
  const borderClass = hideGrids ? "" : "border-l border-r border-ds-border";
  return (
    <>
      <footer className={cn(hideGrids && "pb-8 sm:pb-12")}>
        <div className="mx-auto max-w-[1440px] px-[9px]">
          <div className={cn(borderClass, "p-6")}>
            <div className="mb-8">
              <PublicSearchBar />
            </div>
            <div
              className={cn(
                "text-ds-text-subtle flex flex-col gap-12",
                hideGrids
                  ? "lg:flex-row lg:items-start lg:justify-between"
                  : "md:flex-row md:items-start md:justify-between",
              )}
            >
              <div className="flex shrink-0 flex-col gap-3">
                <p className="leading-label text-xl">&copy; 2026 B3OS. All rights reserved.</p>
                <p className="text-ds-text-subtle text-sm">
                  B3OS by{" "}
                  <Link href="/company" className="hover:text-ds-text-primary underline">
                    B3.fun
                  </Link>
                </p>
                <StatusBadge />
              </div>

              <div
                className={cn(
                  hideGrids
                    ? "grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-x-8"
                    : "flex flex-col gap-12 sm:flex-row sm:gap-6",
                )}
              >
                {footerColumns.map(col => (
                  <nav
                    key={col.title}
                    aria-label={col.title}
                    className={cn("pl-3", hideGrids ? "min-w-0" : "w-[200px]")}
                  >
                    <h3 className="text-ds-text-muted mb-3 font-mono text-base font-normal leading-[1.06]">
                      [{col.title}]
                    </h3>
                    <ul className="flex flex-col gap-3">
                      {col.links.map(link => (
                        <li key={link.label}>
                          {link.action === "contact" ? (
                            <ContactFooterLink className={linkClass}>{link.label}</ContactFooterLink>
                          ) : link.external ? (
                            <a href={link.href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                              {link.label}
                            </a>
                          ) : (
                            <Link href={link.href} className={linkClass}>
                              {link.label}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </nav>
                ))}
              </div>
            </div>
          </div>
        </div>
        {!hideGrids && (
          <div className="relative z-[3] mx-auto flex h-[19px] max-w-[1440px] items-center gap-2">
            <div className="bg-ds-surface-primary flex size-[19px] shrink-0 items-center justify-center rounded-full">
              <div className="bg-ds-neutral-300 size-[6px] rounded-full" />
            </div>
            <div className="bg-ds-border h-px min-w-0 flex-1" />
            <div className="bg-ds-surface-primary flex size-[19px] shrink-0 items-center justify-center rounded-full">
              <div className="bg-ds-neutral-300 size-[6px] rounded-full" />
            </div>
          </div>
        )}
        <div className="mx-auto max-w-[1440px] px-[9px]">
          <div className={cn(borderClass, "px-6 py-[10px]")}>
            <div
              className={cn(
                "relative w-full",
                hideGrids ? "aspect-[1374/700] sm:aspect-[1374/500] md:aspect-[1374/296]" : "aspect-[1374/296]",
              )}
            >
              <img
                src={IMG_LOGO_DEBOSSED}
                alt="B3OS"
                className="absolute inset-0 size-full opacity-100 transition-opacity dark:opacity-10"
              />
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
